import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverHeader,
	PopoverBody,
	PopoverCloseButton,
	VStack,
	Text,
	Button,
	Spinner,
	Badge,
	HStack,
} from '@chakra-ui/react';
import { FaGlobe } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { clientPortalAdminApi } from '../../Api/clientPortalApi';
import HeaderToolbarIconButton, { HeaderToolbarBadge } from './HeaderToolbarIconButton';

const getInvoiceId = (req) => {
	const invoice = req?.invoiceId;
	if (!invoice) return null;
	return typeof invoice === 'object' ? invoice._id : invoice;
};

const ClientPortalActivityHeader = ({ companyId, size = 'sm' }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [isOpen, setIsOpen] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ['client-portal-activity-summary', companyId],
		queryFn: async () => {
			const { data: res } = await clientPortalAdminApi.getActivitySummary(companyId);
			return res;
		},
		enabled: !!companyId,
		staleTime: 60 * 1000,
		refetchInterval: isOpen ? 60 * 1000 : false,
		refetchOnWindowFocus: !isOpen,
	});

	const { data: requests = [] } = useQuery({
		queryKey: ['client-portal-invoice-requests', companyId],
		queryFn: async () => {
			const { data: res } = await clientPortalAdminApi.getPendingInvoiceRequests(companyId);
			return res;
		},
		enabled: !!companyId,
		staleTime: 60 * 1000,
		refetchInterval: isOpen ? 60 * 1000 : false,
		refetchOnWindowFocus: !isOpen,
	});

	const resolveMutation = useMutation({
		mutationFn: ({ requestId, action, companyNote }) =>
			clientPortalAdminApi.resolveInvoiceRequest(companyId, requestId, {
				action,
				companyNote,
				applyStatusUpdate: action === 'approve',
			}),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['client-portal-activity-summary', companyId] });
			queryClient.invalidateQueries({ queryKey: ['client-portal-invoice-requests', companyId] });
		},
	});

	const pendingCount = Math.max(data?.totalPending || 0, requests.length);

	return (
		<Popover placement='bottom-end' isOpen={isOpen} onOpen={() => setIsOpen(true)} onClose={() => setIsOpen(false)}>
			<PopoverTrigger>
				<HeaderToolbarIconButton
					aria-label='Client portal activity'
					icon={<FaGlobe />}
					size={size}
					badge={
						pendingCount > 0 ? (
							<HeaderToolbarBadge>
								{pendingCount > 99 ? '99+' : pendingCount}
							</HeaderToolbarBadge>
						) : null
					}
				/>
			</PopoverTrigger>
			<PopoverContent w='360px'>
				<PopoverHeader fontWeight='semibold'>Client portal activity</PopoverHeader>
				<PopoverCloseButton />
				<PopoverBody>
					{isLoading ? (
						<Spinner size='sm' />
					) : requests.length === 0 ? (
						<Text fontSize='sm' color='gray.500'>
							No pending client portal requests.
						</Text>
					) : (
						<VStack align='stretch' spacing={3}>
							{requests.map((req) => (
								<BoxItem
									key={req._id}
									req={req}
									isLoading={resolveMutation.isPending}
									onApprove={() =>
										resolveMutation.mutate({
											requestId: req._id,
											action: 'approve',
										})
									}
									onReject={() =>
										resolveMutation.mutate({
											requestId: req._id,
											action: 'reject',
											companyNote: 'Unable to update at this time.',
										})
									}
									onOpenInvoice={() => {
										const invoiceId = getInvoiceId(req);
										navigate(
											invoiceId ? `/invoice/view/${invoiceId}` : '/invoices',
										);
									}}
								/>
							))}
						</VStack>
					)}
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};

const BoxItem = ({ req, onApprove, onReject, onOpenInvoice, isLoading }) => (
	<VStack
		align='stretch'
		p={3}
		border='1px solid'
		borderColor='gray.100'
		borderRadius='md'
		spacing={2}>
		<HStack justify='space-between'>
			<Text fontSize='sm' fontWeight='medium'>
				Invoice status request
			</Text>
			<Badge colorScheme='yellow'>pending</Badge>
		</HStack>
		<Text fontSize='xs' color='gray.600'>
			{req.clientId?.companyName} requested{' '}
			<strong>{req.requestedStatus}</strong>
			{req.invoiceId?.invoiceNumber
				? ` for #${req.invoiceId.invoiceNumber}`
				: ''}
		</Text>
		{req.message && (
			<Text fontSize='xs' color='gray.500'>
				"{req.message}"
			</Text>
		)}
		<HStack>
			<Button size='xs' colorScheme='teal' onClick={onApprove} isLoading={isLoading}>
				Approve
			</Button>
			<Button size='xs' variant='outline' onClick={onReject} isLoading={isLoading}>
				Decline
			</Button>
			<Button size='xs' variant='ghost' onClick={onOpenInvoice}>
				View invoice
			</Button>
		</HStack>
	</VStack>
);

export default ClientPortalActivityHeader;
